/**
 * SignupRequestApprovalDialog Component
 *
 * Dialog for approving or rejecting signup requests.
 * - Approve mode: Shows role dropdown and role-specific fields
 * - Reject mode: Shows optional rejection reason textarea
 *
 * Note: Password is auto-generated by backend on approval
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ActionButton, Dropdown, DropdownTrigger, DropdownContentMultiselect } from 'equal-ds-ui';
import { X, Info, AlertTriangle } from 'lucide-react';
import type { SignupRequest, SignupApprovePayload } from '../../../types/signup-requests';
import type { AdminRole } from '../../../types/admin-users';
import type { PurposeRecord } from '../../../types/purpose';
import { adminUsersAPI } from '../../../services/admin-users-api.service';
import { signupRequestsAPI } from '../../../services/signup-requests-api.service';
import { purposeAPI } from '../../../services/purpose-api.service';
import { centralRegistryAPI } from '../../../services/central-registry-api.service';
import ApproveIcon from '../../../assets/icons/approve-icon.svg';
import RejectIcon from '../../../assets/icons/reject-icon.svg';

// CSS for dots loading animation
const dotsLoadingStyles = `
  .dots-loading span {
    animation: dots 1.4s infinite;
    animation-fill-mode: both;
  }
  .dots-loading span:nth-child(2) {
    animation-delay: 0.2s;
  }
  .dots-loading span:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes dots {
    0%, 80%, 100% { opacity: 0; }
    40% { opacity: 1; }
  }
`;

export interface SignupRequestApprovalDialogProps {
  request: SignupRequest | null;
  mode: 'approve' | 'reject';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowToast?: (message: string) => void;
}

// Role-based field requirements
function isFormRequired(roleId: string): boolean {
  return roleId !== '1' && roleId !== '';
}

function isLoanOfficerRole(roleId: string): boolean {
  return roleId !== '1' && roleId !== '2' && roleId !== '';
}

// Validation patterns
const emailRegex = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,20}$/;
const alphaNumRegex = /^[a-zA-Z0-9]+$/;
const alphaRegex = /^[a-zA-Z]+[a-zA-Z ]*$/;

interface RoleSpecificFields {
  division: string;
  dept_code: string;
  dept_owner: string;
  email_business_owner: string;
  demand_num: string;
  purpose: string[];
  channel_type: string[];
}

const DEFAULT_FIELDS: RoleSpecificFields = {
  division: '',
  dept_code: '',
  dept_owner: '',
  email_business_owner: '',
  demand_num: '',
  purpose: [],
  channel_type: [],
};

export const SignupRequestApprovalDialog: React.FC<SignupRequestApprovalDialogProps> = ({
  request,
  mode,
  isOpen,
  onClose,
  onSuccess,
  onShowToast,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Approve mode state
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Role-specific fields state
  const [fields, setFields] = useState<RoleSpecificFields>(DEFAULT_FIELDS);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof RoleSpecificFields, string>>>({});

  // Options for dropdowns (cached across dialog opens)
  const [purposeOptions, setPurposeOptions] = useState<PurposeRecord[]>([]);
  const [channelTypes, setChannelTypes] = useState<string[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  // Reject mode state
  const [rejectionReason, setRejectionReason] = useState('');

  const isApprove = mode === 'approve';

  // Derived role requirements
  const formRequired = useMemo(() => isFormRequired(String(selectedRoleId ?? '')), [selectedRoleId]);
  const loanOfficer = useMemo(() => isLoanOfficerRole(String(selectedRoleId ?? '')), [selectedRoleId]);

  // Load roles and options when dialog opens in approve mode
  useEffect(() => {
    if (!isOpen || !isApprove) return;

    let cancelled = false;

    const loadData = async () => {
      // Load roles (has its own cache guard)
      loadRoles();

      // Load options only if not already loaded
      if (!optionsLoaded) {
        try {
          const [purposeResponse, channelResponse] = await Promise.all([
            purposeAPI.getPurposes({ limit: 200, pageno: 1 }),
            centralRegistryAPI.getChannelTypes(),
          ]);
          // Only update state if not cancelled (dialog still open)
          if (!cancelled) {
            setPurposeOptions(purposeResponse?.data?.rows ?? []);
            setChannelTypes(channelResponse ?? []);
            setOptionsLoaded(true);
          }
        } catch (err) {
          if (!cancelled) {
            console.error('[SignupRequestApprovalDialog] Failed to load options:', err);
          }
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isApprove, optionsLoaded]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSelectedRoleId(null);
      setRejectionReason('');
      setFields(DEFAULT_FIELDS);
      setFieldErrors({});
    }
  }, [isOpen]);

  const loadRoles = async () => {
    if (roles.length > 0) return;
    try {
      setRolesLoading(true);
      const rolesResponse = await adminUsersAPI.getRoles();
      setRoles(rolesResponse);
    } catch (err) {
      console.error('[SignupRequestApprovalDialog] Failed to load roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setRolesLoading(false);
    }
  };


  const handleFieldChange = useCallback((key: keyof RoleSpecificFields, value: string | string[]) => {
    const processedValue = key === 'email_business_owner' && typeof value === 'string'
      ? value.toLowerCase()
      : value;
    setFields(prev => ({ ...prev, [key]: processedValue }));
    // Clear error on change
    setFieldErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  const validateFields = useCallback((): boolean => {
    const errors: Partial<Record<keyof RoleSpecificFields, string>> = {};

    if (formRequired) {
      if (!fields.division.trim()) {
        errors.division = 'Division is required';
      } else if (!alphaRegex.test(fields.division)) {
        errors.division = 'Invalid division name';
      }

      if (!fields.dept_code.trim()) {
        errors.dept_code = 'Department code is required';
      } else if (!alphaNumRegex.test(fields.dept_code)) {
        errors.dept_code = 'Invalid department code';
      }

      if (!fields.dept_owner.trim()) {
        errors.dept_owner = 'Department owner is required';
      } else if (!alphaRegex.test(fields.dept_owner)) {
        errors.dept_owner = 'Invalid department owner';
      }

      if (!fields.email_business_owner.trim()) {
        errors.email_business_owner = 'Business owner email is required';
      } else if (!emailRegex.test(fields.email_business_owner.toLowerCase())) {
        errors.email_business_owner = 'Invalid email format';
      }

      if (loanOfficer) {
        if (!fields.demand_num.trim()) {
          errors.demand_num = 'Demand number is required';
        } else if (!alphaNumRegex.test(fields.demand_num)) {
          errors.demand_num = 'Invalid demand number';
        }

        if (fields.purpose.length === 0) {
          errors.purpose = 'Purpose is required';
        }

        if (fields.channel_type.length === 0) {
          errors.channel_type = 'Channel type is required';
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [fields, formRequired, loanOfficer]);

  const handleApprove = useCallback(async () => {
    if (!request || !selectedRoleId) {
      setError('Please select a role for the user.');
      return;
    }

    if (!validateFields()) {
      setError('Please fill all required fields.');
      return;
    }

    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setError(null);

      const payload: SignupApprovePayload = {
        request_id: request.id,
        assigned_role_id: selectedRoleId,
      };

      // Add role-specific fields if required
      if (formRequired) {
        payload.division = fields.division;
        payload.dept_code = fields.dept_code;
        payload.dept_owner = fields.dept_owner;
        payload.email_business_owner = fields.email_business_owner;

        if (loanOfficer) {
          payload.demand_num = fields.demand_num;
          payload.purpose = fields.purpose;
          payload.channel_type = fields.channel_type;
        }
      }

      await signupRequestsAPI.approveSignupRequest(payload);

      onShowToast?.(`Account successfully created for ${request.profile.name}`);
      onSuccess();
    } catch (err: any) {
      console.error('[SignupRequestApprovalDialog] Approve failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve user.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [request, selectedRoleId, isProcessing, fields, formRequired, loanOfficer, validateFields, onSuccess, onShowToast]);

  const handleReject = useCallback(async () => {
    if (!request) return;
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setError(null);

      await signupRequestsAPI.rejectSignupRequest({
        request_id: request.id,
        rejection_reason: rejectionReason.trim() || undefined,
      });

      onShowToast?.(`Request rejected for ${request.profile.name}`);
      onSuccess();
    } catch (err: any) {
      console.error('[SignupRequestApprovalDialog] Reject failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reject user.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [request, rejectionReason, isProcessing, onSuccess, onShowToast]);

  const handleConfirm = useCallback(() => {
    if (isProcessing) return;
    if (isApprove) {
      handleApprove();
    } else {
      handleReject();
    }
  }, [isApprove, isProcessing, handleApprove, handleReject]);

  // Check if form is valid for button state — uses same regex rules as validateFields
  const isFormValid = useMemo(() => {
    if (!selectedRoleId) return false;
    if (!formRequired) return true;

    if (!fields.division.trim() || !alphaRegex.test(fields.division)) return false;
    if (!fields.dept_code.trim() || !alphaNumRegex.test(fields.dept_code)) return false;
    if (!fields.dept_owner.trim() || !alphaRegex.test(fields.dept_owner)) return false;
    if (!fields.email_business_owner.trim() || !emailRegex.test(fields.email_business_owner.toLowerCase())) return false;

    if (loanOfficer) {
      if (!fields.demand_num.trim() || !alphaNumRegex.test(fields.demand_num)) return false;
      if (fields.purpose.length === 0) return false;
      if (fields.channel_type.length === 0) return false;
    }

    return true;
  }, [selectedRoleId, formRequired, loanOfficer, fields]);

  const purposeSelectOptions = useMemo(
    () => purposeOptions.map((p) => ({
      value: String(p.purpose_code),
      label: `${p.purpose_code} - ${p.purpose_text}`,
    })),
    [purposeOptions]
  );

  const channelSelectOptions = useMemo(
    () => channelTypes.map((c) => ({ value: c, label: c })),
    [channelTypes]
  );

  if (!isOpen || !request) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className={`relative z-10 w-full bg-white rounded-xl shadow-xl p-6 ${formRequired ? 'max-w-lg' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="absolute top-4 right-4 p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-background-tertiary transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with title */}
        <h3 className="text-lg font-semibold text-text-primary">
          {isApprove ? 'Approve User' : 'Reject User'}
        </h3>

        {/* Centered Icon */}
        <div className="flex justify-center my-4">
          {isApprove ? (
            <img src={ApproveIcon} alt="Approve" className="w-20 h-16 object-contain" />
          ) : (
            <img src={RejectIcon} alt="Reject" className="w-16 h-16 object-contain" />
          )}
        </div>

        {/* Main text */}
        <p className="text-center text-text-primary font-medium">
          Are you sure you want to {isApprove ? 'approve' : 'reject'} {request.profile.name}?
        </p>
        <p className="text-center text-sm text-text-secondary mt-1">
          {isApprove
            ? 'Please assign a role and fill required details'
            : 'The user will have to create a fresh account request once rejected'}
        </p>

        {/* Approve Mode: Role Selection and Role-specific Fields */}
        {isApprove && (
          <div className="mt-4 space-y-3">
            {rolesLoading ? (
              <div className="text-sm text-text-secondary text-center">Loading roles...</div>
            ) : (
              <>
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Select Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedRoleId ? String(selectedRoleId) : ''}
                    onChange={(e) => {
                      setSelectedRoleId(e.target.value ? Number(e.target.value) : null);
                      setFieldErrors({});
                    }}
                    disabled={isProcessing}
                    className="w-full border border-border-default rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={String(role.id)}>
                        {role.display_name || role.role_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role-specific Fields */}
                {formRequired && (
                  <>
                    <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      {loanOfficer
                        ? 'This role requires business details and loan officer fields.'
                        : 'This role requires business department details.'}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Division */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          Division <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={fields.division}
                          onChange={(e) => handleFieldChange('division', e.target.value)}
                          placeholder="Enter division"
                          disabled={isProcessing}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            fieldErrors.division ? 'border-red-500' : 'border-border-default'
                          }`}
                        />
                        {fieldErrors.division && (
                          <p className="text-xs text-red-500 mt-0.5">{fieldErrors.division}</p>
                        )}
                      </div>

                      {/* Dept Code */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          Dept Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={fields.dept_code}
                          onChange={(e) => handleFieldChange('dept_code', e.target.value)}
                          placeholder="Enter code"
                          disabled={isProcessing}
                          maxLength={12}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            fieldErrors.dept_code ? 'border-red-500' : 'border-border-default'
                          }`}
                        />
                        {fieldErrors.dept_code && (
                          <p className="text-xs text-red-500 mt-0.5">{fieldErrors.dept_code}</p>
                        )}
                      </div>

                      {/* Dept Owner */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          Dept Owner <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={fields.dept_owner}
                          onChange={(e) => handleFieldChange('dept_owner', e.target.value)}
                          placeholder="Enter owner name"
                          disabled={isProcessing}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            fieldErrors.dept_owner ? 'border-red-500' : 'border-border-default'
                          }`}
                        />
                        {fieldErrors.dept_owner && (
                          <p className="text-xs text-red-500 mt-0.5">{fieldErrors.dept_owner}</p>
                        )}
                      </div>

                      {/* Business Owner Email */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          Business Owner Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={fields.email_business_owner}
                          onChange={(e) => handleFieldChange('email_business_owner', e.target.value)}
                          placeholder="Enter email"
                          disabled={isProcessing}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            fieldErrors.email_business_owner ? 'border-red-500' : 'border-border-default'
                          }`}
                        />
                        {fieldErrors.email_business_owner && (
                          <p className="text-xs text-red-500 mt-0.5">{fieldErrors.email_business_owner}</p>
                        )}
                      </div>

                      {/* Loan Officer Fields */}
                      {loanOfficer && (
                        <>
                          {/* Demand Number */}
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Demand Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={fields.demand_num}
                              onChange={(e) => handleFieldChange('demand_num', e.target.value)}
                              placeholder="Enter demand number"
                              disabled={isProcessing}
                              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                fieldErrors.demand_num ? 'border-red-500' : 'border-border-default'
                              }`}
                            />
                            {fieldErrors.demand_num && (
                              <p className="text-xs text-red-500 mt-0.5">{fieldErrors.demand_num}</p>
                            )}
                          </div>

                          {/* Purpose */}
                          <div className="col-span-2 equal-ds-root">
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Purpose <span className="text-red-500">*</span>
                            </label>
                            <Dropdown>
                              <DropdownTrigger
                                className={`w-full justify-between text-sm ${
                                  fieldErrors.purpose ? 'border-red-500' : ''
                                }`}
                              >
                                {fields.purpose.length > 0
                                  ? `${fields.purpose.length} selected`
                                  : 'Select purpose'}
                              </DropdownTrigger>
                              <DropdownContentMultiselect
                                options={purposeSelectOptions}
                                selectedValues={fields.purpose}
                                onSelectionChange={(values) => handleFieldChange('purpose', values)}
                                enableSelectAll
                                selectAllLabel="Select All"
                                enableSearch
                                searchPlaceholder="Search..."
                                maxHeight="200px"
                                zIndex={10000}
                              />
                            </Dropdown>
                            {fieldErrors.purpose && (
                              <p className="text-xs text-red-500 mt-0.5">{fieldErrors.purpose}</p>
                            )}
                          </div>

                          {/* Channel Type */}
                          <div className="col-span-2 equal-ds-root">
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Channel Type <span className="text-red-500">*</span>
                            </label>
                            <Dropdown>
                              <DropdownTrigger
                                className={`w-full justify-between text-sm ${
                                  fieldErrors.channel_type ? 'border-red-500' : ''
                                }`}
                              >
                                {fields.channel_type.length > 0
                                  ? `${fields.channel_type.length} selected`
                                  : 'Select channel type'}
                              </DropdownTrigger>
                              <DropdownContentMultiselect
                                options={channelSelectOptions}
                                selectedValues={fields.channel_type}
                                onSelectionChange={(values) => handleFieldChange('channel_type', values)}
                                enableSelectAll
                                selectAllLabel="Select All"
                                enableSearch
                                searchPlaceholder="Search..."
                                maxHeight="200px"
                                zIndex={10000}
                              />
                            </Dropdown>
                            {fieldErrors.channel_type && (
                              <p className="text-xs text-red-500 mt-0.5">{fieldErrors.channel_type}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {!formRequired && selectedRoleId && (
                  <div className="flex items-center gap-1.5 text-xs text-text-secondary justify-center">
                    <Info className="w-3.5 h-3.5" />
                    <span>You can change role later through actions</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Reject Mode: Rejection Reason */}
        {!isApprove && (
          <div className="mt-4">
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection (optional)"
              className="w-full px-3 py-2 border border-border-default rounded-lg text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={3}
              maxLength={500}
              disabled={isProcessing}
            />
            <div className="flex items-center gap-1.5 mt-2 text-xs text-text-secondary justify-center">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>This action cannot be undone</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Action button */}
        <style>{dotsLoadingStyles}</style>
        <div className="equal-ds-root mt-4">
          <ActionButton
            intent={isApprove ? 'approve' : 'reject'}
            size="md"
            filled
            className="w-full"
            onClick={handleConfirm}
            disabled={isProcessing || (isApprove && !isFormValid)}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-1">
                {isApprove ? 'Approving' : 'Rejecting'}
                <span className="dots-loading">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            ) : isApprove ? (
              'Approve'
            ) : (
              'Reject'
            )}
          </ActionButton>
        </div>
      </div>
    </div>
  );
};
